import { useAtomValue } from "jotai";
import { FC, useEffect, useRef } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";

import { usePartnerLogin } from "@/apps/partner/hooks/usePartnerLogin";
import { Button } from "@/components/Interface/Button/Button";
import { FormField } from "@/components/Interface/FormFields/FormField/FormField";
import { PasswordField } from "@/components/Interface/FormFields/PasswordField/PasswordField";
import { useAlert } from "@/hooks/useAlert";
import { accessTokenAtom, refreshTokenAtom } from "@/utils/atoms";
import { BACKEND_EMAIL_REGEX } from "@/utils/constants";
import { createLoginErrorHandler } from "@/utils/loginErrorHandler";

export type PartnerLoginFormFields = {
  emailAddress: string;
  password: string;
};

export const PartnerLoginForm: FC = () => {
  const {
    handleSubmit,
    register,
    setError,
    formState: { errors, isSubmitting },
  } = useForm<PartnerLoginFormFields>({
    defaultValues: {
      emailAddress: "",
      password: "",
    },
  });
  const { t } = useTranslation();
  const showAlert = useAlert();
  const accessToken = useAtomValue(accessTokenAtom);
  const refreshToken = useAtomValue(refreshTokenAtom);

  const { isLoading: publicPartnerLoading, loginAsPublicPartner } =
    usePartnerLogin({
      onSuccess: () => {},
      onError: (error) => {
        console.error(error);
      },
    });

  const { isLoading: partnerLoginLoading, loginAsPartner } = usePartnerLogin({
    onError: (error) => {
      // Create an error handler with the current 2FA modal state
      const errorHandler = createLoginErrorHandler(
        setError,
        t,
        showAlert,
        "emailAddress",
      );
      errorHandler(error);
    },
  });

  const attemptedPublicLoginRef = useRef(false);
  useEffect(() => {
    // Only call loginAsPublicPartner if there's no access token and no refresh token
    // This prevents overriding an existing partner session
    if (!accessToken && !refreshToken && !attemptedPublicLoginRef.current) {
      attemptedPublicLoginRef.current = true;
      loginAsPublicPartner();
    }
  }, [accessToken, refreshToken, loginAsPublicPartner]);

  const onSubmit = async (data: PartnerLoginFormFields) => {
    await loginAsPartner({
      email: data.emailAddress,
      password: data.password,
    });
  };

  return (
    <form
      className="flex w-full flex-col gap-6"
      onSubmit={handleSubmit(onSubmit)}
    >
      <h1 className="text-headline-md font-semibold leading-sm text-foreground-primary">
        {t("partnerPortal:welcomeScreen.title")}
      </h1>

      <FormField<PartnerLoginFormFields>
        id="emailAddress"
        name="emailAddress"
        label={t("translation:Common.generalForm.fields.email.label")}
        type="email"
        register={register}
        errors={errors}
        variant="dark"
        options={{
          required: t(
            "translation:Common.generalForm.validation.emailRequired",
          ),
          pattern: {
            value: BACKEND_EMAIL_REGEX,
            message: t(
              "translation:Common.generalForm.validation.emailNotValid",
            ),
          },
        }}
      />
      <PasswordField<PartnerLoginFormFields>
        id="password"
        name="password"
        label={t("translation:Common.generalForm.fields.password.label")}
        register={register}
        errors={errors}
        variant="dark"
        options={{
          required: t(
            "translation:Common.generalForm.validation.passwordRequired",
          ),
        }}
      />

      <Button
        variant="primary"
        type="submit"
        disabled={publicPartnerLoading || partnerLoginLoading || isSubmitting}
      >
        {t("translation:newWelcome.logInForm.submitButton")}
      </Button>
    </form>
  );
};
